import { formatYmdAsCrmDate } from '../utils/dateTime';
import { getDbSupabase } from './supabase';
import type {
  InventoryItem,
  InventoryListMeta,
  InventoryListResponse,
  InventoryOpenPoItem,
  InventoryOpenPoResponse,
  InventoryOpenPoSort,
  InventorySort,
} from '../types/inventory';

export type FetchInventoryParams = {
  ipg?: string | null;
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: InventorySort;
  openPoOnly?: boolean;
  customers?: string | null;
  reportDate?: string | null;
};

export type FetchOpenPoParams = {
  ipg?: string | null;
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: InventoryOpenPoSort;
  signal?: AbortSignal;
};

function num(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function nullableStr(value: unknown): string | null {
  if (value == null || value === '') return null;
  return str(value);
}

function nullableNum(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = num(value);
  return Number.isFinite(n) ? n : null;
}

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  try {
    const { data } = await getDbSupabase('crm').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy rejects unauthenticated in production
  }
  return headers;
}

function normalizeItem(raw: Record<string, unknown>): InventoryItem {
  return {
    customer_no: str(raw.customer_no),
    customer_name: str(raw.customer_name) || str(raw.customer_no),
    salesperson_code: str(raw.salesperson_code),
    ipg: str(raw.ipg),
    item_no: str(raw.item_no),
    description: str(raw.description) || str(raw.item_no),
    inventory: num(raw.inventory),
    fs: num(raw.fs),
    fsm: num(raw.fsm),
    turn_around: nullableNum(raw.turn_around),
    bpo: num(raw.bpo),
    ta: nullableNum(raw.ta),
    ship1_eta: nullableStr(raw.ship1_eta),
    ship1_qty: num(raw.ship1_qty),
    ship1_addons: nullableStr(raw.ship1_addons),
    ship1_ta: nullableNum(raw.ship1_ta),
    bring_forward: nullableStr(raw.bring_forward),
    ship2_eta: nullableStr(raw.ship2_eta),
    ship2_qty: num(raw.ship2_qty),
    ship2_addons: nullableStr(raw.ship2_addons),
    ship2_ta: nullableNum(raw.ship2_ta),
    bring_forward_2: nullableStr(raw.bring_forward_2),
    usage_m1: num(raw.usage_m1),
    usage_m2: num(raw.usage_m2),
    usage_m3: num(raw.usage_m3),
    usage_m4: num(raw.usage_m4),
    remarks: nullableStr(raw.remarks),
    blocked: Boolean(raw.blocked),
  };
}

function normalizeOpenPo(raw: Record<string, unknown>): InventoryOpenPoItem {
  return {
    item_key: str(raw.item_key),
    item_no: str(raw.item_no),
    line_no: num(raw.line_no),
    description: str(raw.description) || str(raw.item_no),
    ipg: str(raw.ipg),
    document_no: str(raw.document_no),
    eta: nullableStr(raw.eta),
    outstanding_qty: num(raw.outstanding_qty),
    vendor_no: str(raw.vendor_no),
  };
}

function normalizeMeta(
  raw: Record<string, unknown>,
  fallback: Partial<InventoryListMeta>,
): InventoryListMeta {
  const labelsRaw = raw.usage_labels;
  const usage_labels = Array.isArray(labelsRaw)
    ? labelsRaw.map((x) => str(x)).filter(Boolean)
    : fallback.usage_labels;
  return {
    total: num(raw.total ?? fallback.total),
    page: num(raw.page ?? fallback.page) || 1,
    page_size: num(raw.page_size ?? fallback.page_size) || 50,
    ipg: raw.ipg == null || raw.ipg === '' ? null : str(raw.ipg),
    search: raw.search == null || raw.search === '' ? null : str(raw.search),
    sort: str(raw.sort, fallback.sort ?? 'inventory_desc'),
    open_po_only: Boolean(raw.open_po_only ?? fallback.open_po_only),
    customers: raw.customers == null || raw.customers === '' ? null : str(raw.customers),
    report_date: raw.report_date == null ? undefined : str(raw.report_date),
    usage_labels,
  };
}

async function parseInventoryResponse(
  response: Response,
  fallback: Partial<InventoryListMeta>,
): Promise<{ itemsRaw: Record<string, unknown>[]; meta: InventoryListMeta }> {
  const json = (await response.json().catch(() => null)) as
    | (Record<string, unknown> & { error?: string; detail?: string; hint?: string })
    | null;

  if (!response.ok) {
    const detail =
      typeof json?.detail === 'string' && json.detail.trim() ? ` (${json.detail.trim()})` : '';
    const hint =
      typeof json?.hint === 'string' && json.hint.trim() ? ` ${json.hint.trim()}` : '';
    throw new Error((json?.error || `Failed to load inventory (${response.status}).`) + detail + hint);
  }
  if (!json || typeof json !== 'object') {
    throw new Error('Invalid inventory response.');
  }

  const itemsRaw = Array.isArray(json.items)
    ? json.items.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    : [];
  const metaRaw =
    json.meta && typeof json.meta === 'object' ? (json.meta as Record<string, unknown>) : {};

  return {
    itemsRaw,
    meta: normalizeMeta(metaRaw, {
      ...fallback,
      total: itemsRaw.length,
    }),
  };
}

export async function fetchInventoryList(
  params: FetchInventoryParams = {},
): Promise<InventoryListResponse> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 50;
  const sort = params.sort ?? 'inventory_desc';
  const qs = new URLSearchParams();
  qs.set('page', String(page));
  qs.set('page_size', String(pageSize));
  qs.set('sort', sort);
  if (params.ipg) qs.set('ipg', params.ipg);
  if (params.search?.trim()) qs.set('search', params.search.trim());
  if (params.openPoOnly) qs.set('open_po_only', '1');
  if (params.customers?.trim()) qs.set('customers', params.customers.trim());
  if (params.reportDate?.trim()) qs.set('report_date', params.reportDate.trim());

  const response = await fetch(`/inventory-api?${qs.toString()}`, {
    headers: await authHeaders(),
  });
  const parsed = await parseInventoryResponse(response, {
    page,
    page_size: pageSize,
    sort,
    open_po_only: Boolean(params.openPoOnly),
  });

  return {
    items: parsed.itemsRaw.map(normalizeItem),
    meta: parsed.meta,
  };
}

/** Reportable ICRE Customer nos (skips FG-OBR/OBX with inventory 0). */
export async function fetchInventoryIcreCustomers(): Promise<string[]> {
  const response = await fetch('/inventory-api?view=icre_customers', {
    headers: await authHeaders(),
  });
  const json = (await response.json().catch(() => ({}))) as {
    customers?: unknown;
    error?: string;
  };
  if (!response.ok) {
    throw new Error(json.error || `Failed to load ICRE customers (HTTP ${response.status}).`);
  }
  const raw = Array.isArray(json.customers) ? json.customers : [];
  const unique = new Set<string>();
  for (const value of raw) {
    const no = String(value ?? '').trim();
    if (!no) continue;
    if (/^(empty|null)$/i.test(no)) continue;
    unique.add(no);
  }
  return [...unique].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/**
 * Join ICRE / Options customer codes for the analysis RPC.
 * Trailing * is added when missing so mother codes (e.g. Y0175) also match
 * ship-tos (Y0175-S…), same as Excel Options wildcards.
 */
export function joinInventoryCustomerPatterns(codes: string[]): string {
  return codes
    .map((c) => c.trim())
    .filter(Boolean)
    .map((c) => (c.endsWith('*') ? c : `${c}*`))
    .join('|');
}

export async function fetchInventoryOpenPo(
  params: FetchOpenPoParams = {},
): Promise<InventoryOpenPoResponse> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 50;
  const sort = params.sort ?? 'eta_asc';
  const qs = new URLSearchParams();
  qs.set('view', 'open_po');
  qs.set('page', String(page));
  qs.set('page_size', String(pageSize));
  qs.set('sort', sort);
  if (params.ipg) qs.set('ipg', params.ipg);
  if (params.search?.trim()) qs.set('search', params.search.trim());

  const response = await fetch(`/inventory-api?${qs.toString()}`, {
    headers: await authHeaders(),
    signal: params.signal,
  });
  const parsed = await parseInventoryResponse(response, {
    page,
    page_size: pageSize,
    sort,
  });

  return {
    items: parsed.itemsRaw.map(normalizeOpenPo),
    meta: parsed.meta,
  };
}

export function formatInventoryQty(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-SG', { maximumFractionDigits: 2 }).format(value);
}

export function formatInventoryWhole(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-SG', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.round(value));
}

/** Display-only: show -B/-Bn instead of -PD/-PDn; other data stays on the real item. */
export function formatInventoryDisplayItemNo(itemNo: string): string {
  const raw = (itemNo || '').trim();
  if (!raw) return '—';
  return raw.replace(/-PD([0-9]*)$/i, '-B$1');
}

/**
 * Display-only: IPG follows the displayed item code.
 * When the code is shown as -B/-Bn (incl. -PD rewritten to -B), FG-PD → FG-XOC.
 * Filters/tabs still use the real IPG from the database.
 */
export function formatInventoryDisplayIpg(ipg: string, itemNo: string): string {
  const group = (ipg || '').trim();
  if (!group) return '—';
  const displayNo = formatInventoryDisplayItemNo(itemNo);
  if (/^FG-PD$/i.test(group) && /-B[0-9]*$/i.test(displayNo)) {
    return 'FG-XOC';
  }
  return group;
}

export function formatInventoryTa(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${Number.isInteger(value) ? String(value) : value.toFixed(1)} mo`;
}

export function formatInventoryDate(value: string | null): string {
  if (!value) return '—';
  const datePart = value.trim().split('T')[0] ?? '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return formatYmdAsCrmDate(datePart);
  return value;
}
